import { Component, inject, Input, OnInit } from "@angular/core";
import { Store as NgrxStore } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { Store } from "@bryntum/core-thin";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { VisitTypeStore } from "@app/services/store/visit-type-store";
import { SorCodeStore } from "@app/services/store/sor-code-store";
import { SorCodeOverride } from "@app/models/rest/sor-code-override";
import { SorCodeOverrideStore } from "@app/services/store/sor-code-override-store";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-sor-code-override-list",
  templateUrl: "./sor-code-override-list.component.html",
  styleUrls: ["./sor-code-override-list.component.scss"],
  imports: [
    MatToolbar,
    MatButton,
    MatIconButton,
    MatIcon,
    GridComponent,
    MatPaginator,
    TranslatePipe
  ]
})
export class SorCodeOverrideListComponent
  extends BaseDataTablePageDirective<SorCodeOverride>
  implements OnInit
{
  public visitTypeListStore = new Store({ data: [] });
  public sorCodeListStore = new Store({ data: [] });

  @Input()
  public columns: object[] = [
    {
      field: "sorCode",
      text: "SOR Code",
      flex: 1,
      renderer: ({ record }: any) => record.sorCode?.code ?? "none",
      editor: {
        type: "dropdown",
        store: this.sorCodeListStore,
        displayField: "code",
        valueField: null
      }
    },
    {
      field: "contracts",
      text: "Contracts",
      flex: 1,
      editor: true,
      tooltipRenderer: () =>
        "Comma-separated contract references, or leave empty to match any"
    },
    {
      field: "workTypes",
      text: "Work Types",
      flex: 1,
      editor: true,
      tooltipRenderer: () =>
        "Comma-separated work type references, or leave empty to match any"
    },
    {
      text: "Override Visit Type",
      field: "visitType",
      flex: 1,
      renderer: ({ record }: any) => record.visitType?.description ?? "none",
      editor: {
        type: "dropdown",
        store: this.visitTypeListStore,
        displayField: "description",
        valueField: null
      }
    }
  ];

  public sorCodeOverrideStore = inject(SorCodeOverrideStore);
  protected store = inject<NgrxStore<any>>(NgrxStore);
  protected dialog = inject(MatDialog);
  protected formBuilder = inject(UntypedFormBuilder);
  protected visitTypeStore = inject(VisitTypeStore);
  protected sorCodeStore = inject(SorCodeStore);
  public serverErrorHandler = inject(ServiceErrorHandlerService);
  protected ngxUiLoaderService = inject(NgxUiLoaderService);

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);

    super(
      systemSearchMetaRestService,
      inject(SorCodeOverrideStore),
      inject<NgrxStore<any>>(NgrxStore),
      inject(MatDialog),
      "SorCodeOverride",
      inject(UntypedFormBuilder),
      inject(ServiceErrorHandlerService),
      inject(NgxUiLoaderService)
    );
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    // Load reference data for dropdown editors
    this.visitTypeStore.loadAll().then((value) => {
      this.visitTypeListStore.add(value.resources);
    });
    this.sorCodeStore.loadAll().then((value) => {
      this.sorCodeListStore.add(value.resources);
    });

    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
  }

  addRow() {
    this.gridStore.insert(0, {
      contracts: "",
      workTypes: "",
      sorCode: null,
      visitType: null
    });
  }

  showHelp(): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: "SOR Code Override Rules",
        message:
          "Override rules let you assign an alternate visit type to a SOR code when a job matches specific contract and/or work type references. Without any overrides, the default visit type configured on the SOR code is always used.",
        messages: [
          "Matching: When a job is released, the system checks its contract reference and work type reference against the override rules for the relevant SOR code. If a matching rule is found, the override visit type is used instead of the default.",
          "Specificity: If multiple rules could match, the most specific rule wins. A rule that matches both contract AND work type takes priority over one matching only contract or only work type.",
          "Wildcards: Leave the Contracts or Work Types field empty to match any value for that dimension. At least one must be specified. For example, leaving Contracts empty and setting Work Types to 'REPAIR' will match any contract with work type REPAIR.",
          "Comma-separated values: You can list multiple values (e.g. 'C001, C002') in the Contracts or Work Types fields. The rule will match if the job's value appears anywhere in the list.",
          "Conflict prevention: The system will not allow two rules that could match the same combination of contract and work type for the same SOR code, as this would create ambiguity."
        ],
        showCancelButton: false
      },
      width: "550px"
    });
  }
}
